/**
 * Экран «Маршрутные листы».
 *
 * Три раздела подряд отвечают на один вопрос логиста: что ещё не уехало, что
 * в пути и что закончено. Листы сгруппированы по московским дням; текущий день
 * раскрыт, прошлые сворачиваются — вся история сразу превратила бы экран
 * в бесконечную ленту.
 *
 * Отбор и поиск считает СЕРВЕР. Фильтровать загруженные строки нельзя: лист
 * со второй страницы иначе исчезал бы из поиска вовсе.
 *
 * Печать сделана обычным CSS `@media print`, без отдельного генератора PDF:
 * лист — это тот же список остановок, а второй движок вёрстки означал бы
 * вторую версию правды о порядке доставки.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { useToast } from '../../ui/ToastProvider';
import {
  Button,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Modal,
  PostcardMark,
  StatusBadge,
  TextInput,
} from '../../ui/components';
import type { CourierOption } from '../deals/courier-picker';
import { CourierCombobox } from '../logistics/CourierCombobox';
import { OrderWindow } from '../logistics/OrderWindow';
import {
  canShip,
  showsShipButton,
  isDayOpen,
  needsCancelWarning,
  SECTION_HINTS,
  SECTION_TITLES,
  SHEET_SECTIONS,
  shipBlockedReason,
  toggleDay,
  type SheetSection,
  type SheetView,
} from './sheets-view';
import {
  conflictLabel,
  formatDate,
  moscowToday,
  ROUTE_STATE_LABELS,
  stopInterval,
  VEHICLE_LABELS,
  type RouteCardView,
} from './routing';
import './routing.css';

interface SheetsResponse {
  days: { date: string; sheets: SheetView[] }[];
  total: number;
  hasMore: boolean;
}

interface CancelResponse {
  createdSheet: { id: string; number: string } | null;
  restoredOrders: number;
  unchanged: boolean;
}

/** Сколько листов раздела грузить за раз. Продолжение — по кнопке. */
const PAGE_SIZE = 20;

/**
 * Состав раскрытого листа.
 *
 * Берёт тот же существующий read-only маршрут `GET /api/routes/:id`, что и
 * печатная форма, — своего контракта не заводит. Факт доставки не выдумывается:
 * он приходит номерами заказов в самом листе (`deliveredNumbers`).
 */
function SheetOrders({
  routeId,
  deliveredNumbers,
  onOpenOrder,
}: {
  routeId: string;
  deliveredNumbers: readonly string[];
  onOpenOrder: (orderId: string) => void;
}): React.JSX.Element {
  const { client, user } = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  /** Заказ, для которого открыто подтверждение удаления из активного листа. */
  const [removeTarget, setRemoveTarget] = useState<{ id: string; number: string } | null>(null);
  /** Явное подтверждение администратора, что заказ не у курьера. */
  const [removeAck, setRemoveAck] = useState(false);
  const closeRemove = (): void => {
    setRemoveTarget(null);
    setRemoveAck(false);
  };
  const card = useQuery({
    queryKey: ['route', routeId],
    queryFn: () => client.get<RouteCardView>(`/api/routes/${routeId}`),
  });

  /**
   * Убирает заказ из УЖЕ отгруженного (или подтверждённого) листа.
   *
   * Ту же серверную ручку сторожит роль ADMIN и проверка финального
   * результата, поэтому клиент лишь показывает кнопку и подтверждение, а
   * последнее слово — за сервером. `expectedVersion` защищает от гонки с
   * чужой правкой листа: устаревшая версия вернёт понятную ошибку.
   */
  const removeFromActive = useMutation({
    mutationFn: (input: { orderId: string; expectedVersion: number }) =>
      client.post(`/api/routes/${routeId}/orders/${input.orderId}/remove-from-active`, {
        expectedVersion: input.expectedVersion,
      }),
    onSuccess: () => {
      showToast('Заказ убран из маршрута и вернулся в «Сделки»', 'success');
      closeRemove();
      void queryClient.invalidateQueries({ queryKey: ['route-sheets'] });
      void queryClient.invalidateQueries({ queryKey: ['route', routeId] });
      void queryClient.invalidateQueries({ queryKey: ['routes'] });
    },
    onError: (error: unknown) => {
      showToast(
        (error as { message?: string }).message ?? 'Не удалось убрать заказ из маршрута',
        'error',
      );
      closeRemove();
      void queryClient.invalidateQueries({ queryKey: ['route-sheets'] });
      void queryClient.invalidateQueries({ queryKey: ['route', routeId] });
    },
  });

  if (card.isPending) {
    return <LoadingState title="Загружаем состав…" />;
  }
  if (card.isError) {
    return <ErrorState title="Не удалось загрузить состав" onRetry={() => void card.refetch()} />;
  }

  const route = card.data;
  const isAdmin = user?.roles.includes('ADMIN') === true;
  /*
   * Убрать заказ из ЖИВОГО листа может только администратор и только пока лист
   * подтверждён или отгружен: у доставленной остановки уже есть исход, и её
   * трогает не эта кнопка. По доставленным ✕ не показываем — сервер их всё
   * равно отклонит (ORDER_HAS_FINAL_RESULT), а гашёная кнопка обещала бы
   * действие, которого нет.
   */
  const canRemoveFromActive = isAdmin && (route.state === 'ACTIVE' || route.state === 'CONFIRMED');
  const delivered = new Set(deliveredNumbers);

  return (
    <>
      <ul className="sheets__orders" data-testid="sheet-orders">
        {route.orders.map((item) => (
          <li
            key={item.routeOrderId}
            className="sheets__order"
            data-testid="sheet-order"
            data-order-number={item.order.number}
          >
            <span className="sheets__order-position">{item.position}</span>
            {/*
              Номер и пометка «(ОТКРЫТКА)» — один блок: пометка читается
              вместе с номером, а на узком экране уходит под него, не
              выталкивая время, адрес и кнопку удаления за край строки.
            */}
            <span className="sheets__order-title">
              {/* Номер — вход в окно заказа: там вся информация и правки. */}
              <button
                type="button"
                className="sheets__order-number order-number-button"
                data-testid="order-number"
                onClick={() => onOpenOrder(item.order.id)}
              >
                {item.order.number}
              </button>
              <PostcardMark hasPostcard={item.order.hasPostcard} />
            </span>
            {/*
              Время стоит между номером и адресом: остановка читается как
              «этот заказ, к этому часу, сюда». Адрес идёт последним — он
              длиннее всех и забирает остаток строки.
            */}
            <span className="sheets__order-interval muted">
              {stopInterval(item.order.interval)}
            </span>
            <span className="sheets__order-address">
              {item.order.address ?? '—'}
              {item.order.addressDetails === null ? '' : ` · ${item.order.addressDetails}`}
            </span>
            {delivered.has(item.order.number) && (
              <span className="sheets__order-state">Доставлен</span>
            )}
            {canRemoveFromActive && !delivered.has(item.order.number) && (
              <button
                type="button"
                className="routes__stop-remove sheets__order-remove"
                data-testid="sheet-order-remove-active"
                aria-label={`Убрать заказ ${item.order.number} из маршрута`}
                title="Убрать из маршрута (только администратор): заказ вернётся в «Сделки»"
                disabled={removeFromActive.isPending}
                onClick={() => setRemoveTarget({ id: item.order.id, number: item.order.number })}
              >
                <X size={14} aria-hidden="true" />
              </button>
            )}
          </li>
        ))}
      </ul>

      <Modal
        open={removeTarget !== null}
        title="Убрать заказ из маршрута?"
        onClose={closeRemove}
        dismissible={!removeFromActive.isPending}
        testId="sheet-order-remove-confirm"
      >
        {removeTarget !== null && (
          <div className="stack">
            <p className="text-sm">
              Заказ {removeTarget.number} будет убран из листа {route.number} и вернётся в «Сделки»
              нераспределённым, а также в «Склад → Ожидают приёмки» для назначения ячейки — без
              пересборки и новой печати, если состав не менялся. Заказ полностью снимается с
              курьера; задача возврата ему не создаётся, а возврат на склад кладовщик оформляет
              обычной приёмкой. Ничего не удаляется. Действие доступно только администратору.
            </p>
            <label className="row" style={{ gap: '8px', alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                data-testid="sheet-order-remove-ack"
                checked={removeAck}
                onChange={(event) => setRemoveAck(event.target.checked)}
              />
              <span className="text-sm">
                Подтверждаю, что заказ фактически не находится у курьера
              </span>
            </label>
            <div className="modal__footer">
              <Button
                onClick={closeRemove}
                disabled={removeFromActive.isPending}
                data-testid="sheet-order-remove-dismiss"
              >
                Отмена
              </Button>
              <Button
                variant="danger"
                disabled={removeFromActive.isPending || !removeAck}
                data-testid="sheet-order-remove-submit"
                onClick={() =>
                  removeFromActive.mutate({
                    orderId: removeTarget.id,
                    expectedVersion: route.version,
                  })
                }
              >
                Убрать из маршрута
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

/** Имя курьера с телефоном, если он есть в загруженном списке. */
function courierLabel(
  courier: { id: string; fullName: string },
  options: readonly { id: string; phone: string | null }[],
): string {
  const phone = options.find((item) => item.id === courier.id)?.phone ?? null;
  return phone === null ? courier.fullName : `${courier.fullName} · ${phone}`;
}

export function RouteSheetsScreen(): React.JSX.Element {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const today = moscowToday();

  /*
   * По умолчанию — сегодняшний день, как в «Сделках» и «Маршрутизации».
   *
   * Пустая дата означала «все дни»: экран открывался списком за всю историю,
   * и сегодняшние листы приходилось искать среди прошлых. Кнопка «Все дни»
   * рядом возвращает прежний вид одним нажатием.
   */
  const [date, setDate] = useState(moscowToday());
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  /** Лист, у которого открыт выбор курьера. `null` — все показывают плитку. */
  const [courierOpenId, setCourierOpenId] = useState<string | null>(null);
  /** Заказ, открытый в окне: логист правит его адрес, интервал и точку. */
  const [orderWindowId, setOrderWindowId] = useState<string | null>(null);
  const [openedDays, setOpenedDays] = useState<ReadonlySet<string>>(new Set());
  const [pages, setPages] = useState<Record<SheetSection, number>>({
    UNSHIPPED: 1,
    SHIPPED: 1,
    DELIVERED: 1,
  });
  /** Лист, созданный отменой незавершённых: о нём сообщается отдельно. */
  const [createdSheet, setCreatedSheet] = useState<{ id: string; number: string } | null>(null);
  const [cancelFor, setCancelFor] = useState<SheetView | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [confirmAll, setConfirmAll] = useState(false);

  const couriers = useQuery({
    queryKey: ['couriers-for-routes'],
    queryFn: () =>
      client.get<{ items: CourierOption[] }>('/api/users?role=COURIER&status=ACTIVE&limit=100'),
  });

  /** Ручную отгрузку включает администратор; логист только видит состояние. */
  const settings = useQuery({
    queryKey: ['planning-settings'],
    queryFn: () =>
      client.get<{ manualIssue: { value: { enabled: boolean } } }>('/api/settings/planning'),
  });
  const manualIssueEnabled = settings.data?.manualIssue.value.enabled ?? false;

  /*
   * Отдельный запрос на раздел, объявленный явно.
   *
   * Хуки нельзя создавать в цикле или помощнике: их число и порядок обязаны
   * совпадать от рендера к рендеру, иначе React снимает приложение целиком.
   */
  const queryFor = (section: SheetSection, page: number) => ({
    queryKey: ['route-sheets', section, date, search, page] as const,
    queryFn: () => {
      const params = new URLSearchParams({
        section,
        limit: String(PAGE_SIZE * page),
        offset: '0',
      });
      if (date !== '') {
        params.set('deliveryDate', date);
      }
      if (search.trim() !== '') {
        params.set('search', search.trim());
      }
      return client.get<SheetsResponse>(`/api/route-sheets?${params.toString()}`);
    },
  });

  const unshipped = useQuery(queryFor('UNSHIPPED', pages.UNSHIPPED));
  const shipped = useQuery(queryFor('SHIPPED', pages.SHIPPED));
  const delivered = useQuery(queryFor('DELIVERED', pages.DELIVERED));

  const sections = { UNSHIPPED: unshipped, SHIPPED: shipped, DELIVERED: delivered };

  const refreshAll = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['route-sheets'] });
    void queryClient.invalidateQueries({ queryKey: ['routes'] });
  };

  const assignCourier = useMutation({
    mutationFn: (input: { sheet: SheetView; courierUserId: string | null }) =>
      client.put(`/api/routes/${input.sheet.id}/courier`, {
        courierUserId: input.courierUserId,
        expectedVersion: input.sheet.version,
      }),
    onSuccess: () => {
      showToast('Курьер сохранён', 'success');
      refreshAll();
    },
    onError: (error: unknown) => {
      /*
       * Чужая правка — не ошибка человека: данные обновляются, а сообщение
       * объясняет, почему его выбор не сохранился.
       */
      showToast(
        (error as { message?: string }).message ??
          'Лист изменён другим пользователем. Данные обновлены, повторите выбор.',
        'error',
      );
      refreshAll();
    },
  });

  const ship = useMutation({
    mutationFn: (sheet: SheetView) =>
      client.post(`/api/routes/${sheet.id}/ship`, { expectedVersion: sheet.version }),
    onSuccess: () => {
      showToast('Маршрутный лист отгружен', 'success');
      refreshAll();
    },
    onError: (error: unknown) =>
      showToast((error as { message?: string }).message ?? 'Не удалось отгрузить лист', 'error'),
  });

  const returnToDraft = useMutation({
    mutationFn: (sheet: SheetView) =>
      client.post(`/api/routes/${sheet.id}/return-to-draft`, {
        expectedVersion: sheet.version,
        reason: 'Возврат в черновик из маршрутных листов',
      }),
    onSuccess: () => {
      showToast('Лист вернулся в «Маршрутизацию» без потери состава', 'success');
      refreshAll();
    },
    onError: (error: unknown) =>
      showToast((error as { message?: string }).message ?? 'Не удалось вернуть лист', 'error'),
  });

  const cancelShipment = useMutation({
    mutationFn: (input: { sheet: SheetView; mode: 'UNFINISHED' | 'ALL' }) =>
      client.post<CancelResponse>(`/api/routes/${input.sheet.id}/cancel-shipment`, {
        expectedVersion: input.sheet.version,
        mode: input.mode,
        ...(input.mode === 'ALL' ? { reason: cancelReason.trim() } : {}),
      }),
    onSuccess: (result) => {
      setCancelFor(null);
      setConfirmAll(false);
      setCancelReason('');
      refreshAll();
      /*
       * Номер нового листа приходит с сервера и показывается ТОЛЬКО после
       * успешной операции: браузеру его вычислять неоткуда, а при ошибке
       * показывать нечего.
       */
      if (result.createdSheet !== null) {
        setCreatedSheet(result.createdSheet);
      } else {
        showToast('Отгрузка отменена', 'success');
      }
    },
    onError: (error: unknown) =>
      showToast((error as { message?: string }).message ?? 'Не удалось отменить отгрузку', 'error'),
  });

  const busy =
    ship.isPending ||
    returnToDraft.isPending ||
    cancelShipment.isPending ||
    assignCourier.isPending;

  const sheetCard = useQuery({
    queryKey: ['route', openId],
    queryFn: () => client.get<RouteCardView>(`/api/routes/${openId ?? ''}`),
    enabled: openId !== null,
  });

  return (
    <section className="stack">
      {/*
        Отбор одной строкой.

        Подписи «День» и «Поиск» занимали по строке каждая и повторяли то,
        что и так видно: в поле даты стоит дата, в поле поиска — подсказка.
        Освободившееся место ушло списку. Доступные имена полей остались
        на месте: их читают с экрана, даже когда подписи не видно.
      */}
      <div className="no-print routes__sheet-filters">
        <TextInput
          type="date"
          value={date}
          aria-label="День"
          data-testid="sheets-date"
          onChange={(event) => setDate(event.target.value)}
        />
        {/* Пустая дата — все дни. Кнопка называет это словами, а не подписью под полем. */}
        <Button
          variant="secondary"
          disabled={date === ''}
          data-testid="sheets-all-days"
          onClick={() => setDate('')}
        >
          Все дни
        </Button>
        <TextInput
          value={search}
          aria-label="Поиск"
          placeholder="Номер листа, номер заказа, имя или телефон курьера"
          data-testid="sheets-search"
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {/*
        Уведомление о созданном листе.

        Появляется только после успешного разделения и ведёт ровно в тот лист,
        который создан. Персональных данных здесь нет — только номер.
      */}
      {createdSheet !== null && (
        <p className="routes__hint no-print" role="status" data-testid="sheets-created-notice">
          Незавершённые заказы перенесены в новый маршрутный лист {createdSheet.number}.{' '}
          <button
            type="button"
            className="deals__link"
            data-testid="sheets-open-created"
            onClick={() => {
              setOpenId(createdSheet.id);
              setCreatedSheet(null);
            }}
          >
            Открыть МЛ {createdSheet.number}
          </button>
        </p>
      )}

      {SHEET_SECTIONS.map((section) => {
        const query = sections[section];
        const days = query.data?.days ?? [];
        return (
          <section key={section} className="routes__section" data-testid={`sheets-${section}`}>
            <h3 className="routes__section-title">
              {/* Точка состояния: раздел опознаётся цветом раньше, чем прочитан. */}
              <span className="routes__section-dot" aria-hidden="true" />
              {SECTION_TITLES[section]}{' '}
              <span className="routes__section-count">{query.data?.total ?? 0}</span>
              <span className="routes__section-hint">{SECTION_HINTS[section]}</span>
            </h3>

            {query.isPending ? (
              <LoadingState title="Загружаем листы…" />
            ) : query.isError ? (
              <ErrorState title="Не удалось загрузить листы" onRetry={() => void query.refetch()} />
            ) : days.length === 0 ? (
              <EmptyState title="Листов в этом разделе нет" />
            ) : (
              days.map((day) => {
                const open = isDayOpen(day.date, today, openedDays);
                return (
                  <div key={day.date} className="routes__day" data-testid="sheets-day">
                    <button
                      type="button"
                      className="routes__day-toggle"
                      aria-expanded={open}
                      data-testid="sheets-day-toggle"
                      data-day={day.date}
                      onClick={() => setOpenedDays((current) => toggleDay(current, day.date))}
                    >
                      {formatDate(day.date)}
                      <span className="muted text-sm"> · листов: {day.sheets.length}</span>
                    </button>

                    {open && (
                      <ul className="routes__list">
                        {day.sheets.map((sheet) => (
                          <li
                            key={sheet.id}
                            /*
                              Лист без курьера отмечен фоном: в разделе он один
                              требует действия, и искать его глазами по пустому
                              полю курьера — лишняя работа.
                            */
                            className={
                              section === 'UNSHIPPED' && sheet.courier === null
                                ? 'routes__list-item sheets__item sheets__item--needs-courier'
                                : 'routes__list-item sheets__item'
                            }
                            data-testid="sheet-row"
                            data-sheet-number={sheet.number}
                            data-expanded={expandedId === sheet.id ? 'true' : 'false'}
                          >
                            <div className="sheets__item-head">
                              <div className="sheets__item-main">
                                {/*
                                  Свёрнутый лист — ровно одна строка: номер,
                                  состояние и счётчики. Скрытого пустого тела
                                  под ней нет, состав грузится только при
                                  раскрытии.
                                */}
                                <button
                                  type="button"
                                  className="sheets__item-toggle"
                                  aria-expanded={expandedId === sheet.id}
                                  data-testid="sheet-expand"
                                  onClick={() =>
                                    setExpandedId((current) =>
                                      current === sheet.id ? null : sheet.id,
                                    )
                                  }
                                >
                                  <span className="routes__number">{sheet.number}</span>
                                  <StatusBadge tone="info">
                                    {
                                      ROUTE_STATE_LABELS[
                                        sheet.state as keyof typeof ROUTE_STATE_LABELS
                                      ]
                                    }
                                  </StatusBadge>
                                  <span className="muted text-sm">
                                    заказов {sheet.totalOrders}
                                    {sheet.deliveredOrders > 0
                                      ? ` · доставлено ${sheet.deliveredOrders}`
                                      : ''}
                                  </span>
                                  {/*
                                    Слово вместо стрелки: стрелка не называет,
                                    что именно раскроется, и в ряду с другими
                                    значками читается как украшение.
                                  */}
                                  <span className="sheets__item-toggle-text">
                                    {expandedId === sheet.id ? 'скрыть' : 'состав'}
                                  </span>
                                </button>
                                {/*
                                Курьер выбирается прямо в листе тем же контролом,
                                что и на других вкладках: нажатие в поле открывает
                                список, ввод его сужает. Список рисуется поверх
                                содержимого и карточку не растягивает.
                              */}
                                {section === 'UNSHIPPED' ? (
                                  <div
                                    className="routes__sheet-courier"
                                    data-testid="sheet-courier"
                                  >
                                    {/*
                                      Сначала ответ, потом инструмент.

                                      Раскрытый список занимал строку у каждого
                                      листа, даже когда курьера не меняют,
                                      а главный вопрос — назначен он или нет —
                                      приходилось вычитывать из поля ввода.
                                      Плитка отвечает сразу, выбор открывается
                                      по требованию.
                                    */}
                                    {courierOpenId === sheet.id ? (
                                      <CourierCombobox
                                        options={couriers.data?.items ?? []}
                                        value={
                                          sheet.courier === null
                                            ? null
                                            : ((couriers.data?.items ?? []).find(
                                                (item) => item.id === sheet.courier?.id,
                                              ) ?? {
                                                id: sheet.courier.id,
                                                fullName: sheet.courier.fullName,
                                                phone: null,
                                              })
                                        }
                                        disabled={busy}
                                        testId="sheet-courier-combobox"
                                        onChange={(courier) => {
                                          assignCourier.mutate({
                                            sheet,
                                            courierUserId: courier === null ? null : courier.id,
                                          });
                                          setCourierOpenId(null);
                                        }}
                                      />
                                    ) : (
                                      <button
                                        type="button"
                                        className={
                                          sheet.courier === null
                                            ? 'routes__courier-pill routes__courier-pill--empty'
                                            : 'routes__courier-pill'
                                        }
                                        disabled={busy}
                                        aria-expanded={false}
                                        data-testid="sheet-courier-pick"
                                        onClick={() => setCourierOpenId(sheet.id)}
                                      >
                                        <span className="routes__courier-pill-name">
                                          {/*
                                            Телефон берётся из загруженного
                                            списка: в самом листе его нет,
                                            а по нему курьера и зовут. Нет
                                            в списке — показываем одно имя.
                                          */}
                                          {sheet.courier === null
                                            ? 'Курьер не назначен'
                                            : courierLabel(
                                                sheet.courier,
                                                couriers.data?.items ?? [],
                                              )}
                                        </span>
                                        <span className="routes__courier-pill-action">
                                          {sheet.courier === null ? 'выбрать' : 'сменить'}
                                        </span>
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <div className="muted text-sm" data-testid="sheet-courier">
                                    Курьер: {sheet.courier?.fullName ?? 'не назначен'}
                                  </div>
                                )}
                              </div>

                              <div className="routes__actions">
                                {section === 'UNSHIPPED' && (
                                  <>
                                    {/*
                                      Кнопки нет вовсе, пока ручная отгрузка
                                      выключена: погашенная кнопка обещала бы
                                      действие, которого в этом контуре не
                                      существует.
                                    */}
                                    {showsShipButton(manualIssueEnabled) && (
                                      <Button
                                        variant="primary"
                                        disabled={busy || !canShip(sheet, manualIssueEnabled)}
                                        title={
                                          shipBlockedReason(sheet, manualIssueEnabled) ?? undefined
                                        }
                                        data-testid="sheet-ship"
                                        onClick={() => ship.mutate(sheet)}
                                      >
                                        Отгрузить
                                      </Button>
                                    )}
                                    <Button
                                      disabled={busy}
                                      data-testid="sheet-return-to-draft"
                                      onClick={() => returnToDraft.mutate(sheet)}
                                    >
                                      Вернуть в черновик
                                    </Button>
                                  </>
                                )}
                                {/*
                                  Вход в маршрутный лист.

                                  Кнопка убиралась как лишняя, но за ней стоит
                                  единственная в продукте печатная форма листа:
                                  без неё курьеру нечего взять с собой на бумаге.
                                */}
                                <Button
                                  data-testid="sheet-open"
                                  onClick={() => setOpenId(sheet.id)}
                                >
                                  Открыть лист
                                </Button>
                                {section === 'SHIPPED' && (
                                  <Button
                                    variant="danger"
                                    disabled={busy}
                                    data-testid="sheet-cancel-shipment"
                                    onClick={() => {
                                      setCancelFor(sheet);
                                      setCancelReason('');
                                      setConfirmAll(false);
                                    }}
                                  >
                                    Отменить отгрузку
                                  </Button>
                                )}
                              </div>
                            </div>

                            {expandedId === sheet.id && (
                              <SheetOrders
                                routeId={sheet.id}
                                deliveredNumbers={sheet.deliveredNumbers}
                                onOpenOrder={setOrderWindowId}
                              />
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })
            )}

            {(query.data?.hasMore ?? false) && (
              <Button
                data-testid="sheets-more"
                onClick={() =>
                  setPages((current) => ({ ...current, [section]: current[section] + 1 }))
                }
              >
                Показать ещё
              </Button>
            )}
          </section>
        );
      })}

      {/*
        Отмена отгрузки.

        Без доставленных заказов это обычное подтверждение. С доставленными —
        предупреждение с их номерами и тремя разными исходами: закрыть, вернуть
        только незавершённые или вернуть всё административной коррекцией.
      */}
      <Modal
        open={cancelFor !== null}
        title={
          cancelFor !== null && needsCancelWarning(cancelFor)
            ? 'В листе есть доставленные заказы'
            : 'Отменить отгрузку'
        }
        onClose={() => setCancelFor(null)}
        dismissible={!cancelShipment.isPending}
        testId="cancel-shipment-dialog"
      >
        {cancelFor !== null && (
          <div className="stack">
            {needsCancelWarning(cancelFor) ? (
              <>
                <p className="text-sm">
                  Уже доставлены заказы: {cancelFor.deliveredNumbers.join(', ')}. Выберите, что с
                  ними делать.
                </p>

                <Field label="Причина" hint="Обязательна для возврата доставленных заказов">
                  {(props) => (
                    <TextInput
                      {...props}
                      value={cancelReason}
                      data-testid="cancel-reason"
                      disabled={cancelShipment.isPending}
                      onChange={(event) => setCancelReason(event.target.value)}
                    />
                  )}
                </Field>

                {confirmAll && (
                  <p className="text-sm" data-testid="cancel-all-confirm">
                    Доставленные заказы снова станут неотгруженными. Прежние факты доставки
                    останутся в истории отменёнными. Подтвердите ещё раз.
                  </p>
                )}

                <div className="modal__footer">
                  <Button
                    onClick={() => setCancelFor(null)}
                    disabled={cancelShipment.isPending}
                    data-testid="cancel-dismiss"
                  >
                    Отмена
                  </Button>
                  <Button
                    disabled={cancelShipment.isPending}
                    data-testid="cancel-unfinished"
                    onClick={() => cancelShipment.mutate({ sheet: cancelFor, mode: 'UNFINISHED' })}
                  >
                    Отменить отгрузку незавершённых
                  </Button>
                  <Button
                    variant="danger"
                    disabled={cancelShipment.isPending || cancelReason.trim().length < 3}
                    data-testid="cancel-all"
                    onClick={() => {
                      if (!confirmAll) {
                        setConfirmAll(true);
                        return;
                      }
                      cancelShipment.mutate({ sheet: cancelFor, mode: 'ALL' });
                    }}
                  >
                    {confirmAll ? 'Подтвердить: отменить все' : 'Отменить все'}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm">
                  Лист {cancelFor.number} вернётся в неотгруженные. Состав и порядок сохранятся.
                </p>
                <div className="modal__footer">
                  <Button onClick={() => setCancelFor(null)} data-testid="cancel-dismiss">
                    Отмена
                  </Button>
                  <Button
                    variant="primary"
                    disabled={cancelShipment.isPending}
                    data-testid="cancel-confirm"
                    onClick={() => cancelShipment.mutate({ sheet: cancelFor, mode: 'UNFINISHED' })}
                  >
                    Отменить отгрузку
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/*
        Лист открывается отдельным окном.

        Прежде он дорисовывался под всем списком: чтобы его увидеть, нужно было
        прокрутить мимо всех остальных листов, и связь с той строкой, которую
        открыли, терялась.
      */}
      <Modal
        open={openId !== null}
        title="Маршрутный лист"
        className="sheet-modal"
        testId="sheet-window"
        onClose={() => setOpenId(null)}
      >
        <>
          {sheetCard.isPending ? (
            <LoadingState title="Готовим маршрутный лист…" />
          ) : sheetCard.isError || sheetCard.data === undefined ? (
            <ErrorState
              title="Не удалось загрузить лист"
              onRetry={() => void sheetCard.refetch()}
            />
          ) : (
            <article className="sheet">
              <header className="sheet__header">
                <div>
                  <h3>Маршрутный лист {sheetCard.data.number}</h3>
                  <p className="text-sm">
                    Дата: {formatDate(sheetCard.data.deliveryDate)} · Транспорт:{' '}
                    {VEHICLE_LABELS[sheetCard.data.vehicleType]} · Курьер:{' '}
                    {sheetCard.data.courier?.fullName ?? 'не назначен'}
                  </p>
                </div>
                <div className="no-print sheet__controls">
                  <Button variant="primary" onClick={() => window.print()}>
                    Печать
                  </Button>
                  <Button onClick={() => setOpenId(null)}>Закрыть</Button>
                </div>
              </header>

              <ol className="sheet__stops">
                {sheetCard.data.orders.map((item) => (
                  <li key={item.routeOrderId} className="sheet__stop">
                    <div className="sheet__stop-head">
                      <span className="sheet__position">{item.position}</span>
                      <span className="routes__number">{item.order.number}</span>
                      <span>{stopInterval(item.order.interval)}</span>
                    </div>
                    <div className="sheet__stop-line">
                      <strong>{item.order.address ?? '—'}</strong>
                    </div>
                    {/*
                      Печатная форма показывает ОБА значения: по адресу курьер
                      едет, по деталям находит дверь. Лист печатают и берут
                      с собой — спросить недостающее в пути будет не у кого.
                    */}
                    {item.order.addressDetails !== null && (
                      <div className="sheet__stop-line">{item.order.addressDetails}</div>
                    )}
                    <div className="sheet__stop-line">{item.order.recipient ?? '—'}</div>
                    {item.order.comment !== null && (
                      <div className="sheet__stop-line">Комментарий: {item.order.comment}</div>
                    )}
                    <div className="sheet__stop-line">
                      {item.order.cashToCollect === null
                        ? 'Наличные не принимаются'
                        : `К получению: ${item.order.cashToCollect} ₽`}
                    </div>
                    {(item.order.needsAttention || item.conflicts.length > 0) && (
                      <div className="sheet__warnings">
                        {item.order.needsAttention && <span>Требует внимания</span>}
                        {item.conflicts.map((conflict) => (
                          <span key={conflict.kind}>{conflictLabel(conflict.kind)}</span>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ol>

              {sheetCard.data.orders.length === 0 && (
                <p className="text-sm">В маршруте нет заказов.</p>
              )}

              <footer className="sheet__footer text-sm">
                Итого остановок: {sheetCard.data.orders.length}. Состояние маршрута:{' '}
                <StatusBadge tone="info">
                  {ROUTE_STATE_LABELS[sheetCard.data.state].toLocaleLowerCase('ru')}
                </StatusBadge>
              </footer>
            </article>
          )}
        </>
      </Modal>

      {/* Окно заказа: одно и то же на всех вкладках. */}
      {orderWindowId !== null && (
        <OrderWindow orderId={orderWindowId} onClose={() => setOrderWindowId(null)} />
      )}
    </section>
  );
}
